import React, { useState, useRef, useEffect } from 'react';
import { 
  QuestionMarkCircleIcon, 
  XMarkIcon,
  PlayIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  BookOpenIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Chatbot from './Chatbot';

interface HelpWidgetProps {
  onStartTour?: () => void;
}

export default function HelpWidget({ onStartTour }: HelpWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [userName] = useState('Adam'); // This would come from user context
  const widgetRef = useRef<HTMLDivElement>(null);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleGetStarted = () => {
    console.log('Starting onboarding walkthrough...');
    if (onStartTour) {
      onStartTour();
    }
    setIsOpen(false);
  };

  const handleAskQuestion = () => {
    console.log('Opening chatbot...');
    setShowChatbot(true);
  };

  const handleBackToMenu = () => {
    setShowChatbot(false);
  };

  const handleDocumentation = () => {
    console.log('Opening documentation...');
    // This would open documentation in new tab or modal
    window.open('/docs', '_blank');
    setIsOpen(false);
  };

  const handleHelpGuides = () => {
    console.log('Opening help guides...');
    // Open docs page with help guides tab
    window.open('/docs?tab=help-guides', '_blank');
    setIsOpen(false);
  };

  const helpOptions = [
    {
      id: 'get-started',
      icon: PlayIcon,
      title: 'Get Started',
      description: 'Take a tour of the platform',
      onClick: handleGetStarted
    },
    {
      id: 'ask-question',
      icon: ChatBubbleLeftRightIcon,
      title: 'Ask a Question',
      description: 'Chat with our support team',
      onClick: handleAskQuestion
    },
    {
      id: 'documentation',
      icon: DocumentTextIcon,
      title: 'Documentation',
      description: 'Browse our complete docs',
      onClick: handleDocumentation
    },
    {
      id: 'help-guides',
      icon: BookOpenIcon,
      title: 'Help Guides',
      description: 'Step-by-step tutorials',
      onClick: handleHelpGuides
    }
  ];

  return (
    <div className="help-widget" ref={widgetRef}>
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`help-button ${isOpen ? 'active' : ''}`}
        aria-label="Help"
      >
        <QuestionMarkCircleIcon className="w-5 h-5" />
        <span>Help</span>
      </button>

      {/* Help Popup */}
      {isOpen && (
        <div className="help-popup">
          {/* Header */}
          <div className="help-header">
            <div className="help-logo">
              <img src="/icon.png" alt="Certs" className="logo-image" />
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="help-close"
              aria-label="Close help"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Conditional Content */}
          {showChatbot ? (
            /* Chatbot Interface */
            <Chatbot onBack={handleBackToMenu} />
          ) : (
            <>
              {/* Welcome Message */}
              <div className="help-welcome">
                <h3 className="welcome-greeting">{getGreeting()} {userName}.</h3>
                <p className="welcome-subtitle">How can we help?</p>
              </div>

              {/* Help Options */}
              <div className="help-options">
                {helpOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={option.onClick}
                      className="help-option"
                    >
                      <div className="option-icon">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="option-content">
                        <span className="option-title">{option.title}</span>
                        <span className="option-description">{option.description}</span>
                      </div>
                      <div className="option-arrow">
                        <ChevronRightIcon className="w-4 h-4" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
}
